import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Printer } from 'lucide-react'
import toast from 'react-hot-toast'

import { branchService } from '@/services/branchService'
import { reportService, TrialBalanceEntry } from '@/services/reportService'
import { BranchEntity } from '@/api/types'
import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'

const formatAmount = (value?: number | null) => {
  if (value === undefined || value === null || value === 0) return '-'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const buildAddress = (entity?: Partial<BranchEntity> | null) => {
  if (!entity) return ''
  const source = entity as Record<string, any>
  const parts = [
    source.address,
    source.addressEn,
    source.addressAr,
    source.street,
    source.citySubdivision,
    source.citySubdivisionAr,
    source.cityName,
    source.cityNameAr,
    source.city,
    source.countryName,
    source.countryNameAr,
    source.countryAr,
    source.zipCode,
    source.postalCode,
  ]
  return parts.filter((part) => typeof part === 'string' && part.trim().length > 0).join(', ')
}

export default function TrialBalanceReportPrint(): JSX.Element {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const fromDate = searchParams.get('fromDate') || ''
  const toDate = searchParams.get('toDate') || ''
  const branchIdParam = searchParams.get('branchId')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [trialBalanceData, setTrialBalanceData] = useState<TrialBalanceEntry[]>([])
  const [ledgerTotals, setLedgerTotals] = useState({
    oDebit: 0,
    oCredit: 0,
    oTotal: 0,
    aDebit: 0,
    aCredit: 0,
    aTotal: 0,
    cDebit: 0,
    cCredit: 0,
    cTotal: 0,
  })
  const [branch, setBranch] = useState<BranchEntity | null>(null)

  useEffect(() => {
    if (!fromDate || !toDate || !branchIdParam) {
      setError('Missing required parameters')
      setLoading(false)
      return
    }

    const loadReport = async () => {
      try {
        setLoading(true)
        setError(null)

        const branchId = Number(branchIdParam)

        // Convert dates to ISO format
        const fromDateTime = fromDate.includes('T') ? fromDate : `${fromDate}T00:00:00`
        const toDateTime = toDate.includes('T') ? toDate : `${toDate}T23:59:59`

        const [reportData, branchData] = await Promise.all([
          reportService.getTrialBalanceReport({
            fromDate: fromDateTime,
            toDate: toDateTime,
            branchId,
            pageNumber: 1,
            pageSize: 10000, // Large page size to get all data for print
          }),
          branchService.getBranchById(branchId).catch(() => null),
        ])

        setTrialBalanceData(reportData?.data || [])
        setLedgerTotals({
          oDebit: reportData?.totalODebit ?? 0,
          oCredit: reportData?.totalOCredit ?? 0,
          oTotal: reportData?.totalOTotal ?? 0,
          aDebit: reportData?.totalADebit ?? 0,
          aCredit: reportData?.totalACredit ?? 0,
          aTotal: reportData?.totalATotal ?? 0,
          cDebit: reportData?.totalCDebit ?? reportData?.totalDebit ?? 0,
          cCredit: reportData?.totalCCredit ?? reportData?.totalCredit ?? 0,
          cTotal: reportData?.totalCTotal ?? reportData?.overallBalance ?? 0,
        })
        setBranch(branchData)
      } catch (err: any) {
        // Error handled silently
        const message = err?.response?.data?.message || err?.message || 'Unable to load report'
        setError(message)
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    loadReport()
  }, [fromDate, toDate, branchIdParam])

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Preparing report for print...</p>
        </div>
      </div>
    )

  if (error || !trialBalanceData)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <p className="mb-4 text-lg font-semibold text-destructive">{error || 'Report not found'}</p>
        <Button onClick={() => navigate('/reports/trial-balance')}>Back to Report</Button>
      </div>
    )

  return (
    <div className="trial-balance-print-root p-6 bg-gray-50 print:bg-white print:p-0">
      {/* Print styles embedded */}
      <style>{`
        @media print {
          /* ✅ A3 Landscape – accounting reports ke liye must */
          @page {
            size: A3 landscape;
            margin: 5mm;
          }

          /* ✅ Chrome PDF scaling fix */
          body {
            zoom: 0.8; /* agar phir bhi tight ho to 0.75 */
          }

          * {
            box-sizing: border-box;
          }

          html, body {
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            overflow: visible;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .no-print {
            display: none !important;
          }

          .trial-balance-print-root,
          .trial-balance-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          /* ✅ TABLE FIX – MOST IMPORTANT */
          table {
            width: 100% !important;
            table-layout: fixed !important; /* PDF engine ko force karta hai */
            border-collapse: collapse;
          }

          thead {
            display: table-header-group;
          }

          tfoot {
            display: table-footer-group;
          }

          tr {
            page-break-inside: avoid !important;
          }

          /* ✅ Column width overrides (PDF safe) */
          .trial-balance-table th,
          .trial-balance-table td {
            width: auto !important;
            white-space: nowrap;
            font-size: 11px !important;
            padding: 6px 5px !important;
          }

          /* Text columns wrap ho sakti hain */
          .trial-balance-table td:nth-child(2),
          .trial-balance-table td:nth-child(5),
          .trial-balance-table td:nth-child(6) {
            white-space: normal !important;
            word-break: break-word;
          }

          /* Total row styling */
          .trial-balance-total-row {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            background: #FEF3C7 !important;
            font-weight: 800 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .trial-balance-total-row td {
            background: #FEF3C7 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        .trial-balance-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937; width: 100% }
        .trial-balance-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: 0.5px solid #ddd; width: 100% }
        .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px; width: 100% }
        .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
        .trial-balance-main-title { text-align:center; font-size:16px; font-weight:800; margin-top:4px }
        .divider-strong { height:3px; background:#0A1E60; margin:6px 0; width: 100% }

        /* Trial Balance table */
        .trial-balance-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px !important;  /* DATA TEXT SIZE 11px */
          margin-top: 4px;
          table-layout: auto !important;
        }
        /* ROW HEIGHT (MORE SPACING VERTICAL) */
        .trial-balance-table th,
        .trial-balance-table td {
          padding: 6px 5px !important;  /* MORE SPACING */
          line-height: 1.6 !important;  /* VERTICAL GAP LIKE SAMPLE */
          border: 1px solid #EAEAEA;
        }
        /* HEADER FONT SIZE 12–14px */
        .trial-balance-table thead th {
          background: #F5F5F5;
          font-weight: 700;
          font-size: 13px !important;   /* HEADER TEXT SIZE */
          text-align: center;
        }
        .trial-balance-table tbody tr td {
          vertical-align: middle;
          font-size: 11px !important;
          border: 1px solid #EAEAEA;
        }
        .trial-balance-table tbody tr td.text-right { text-align:right; white-space: nowrap; }
        .trial-balance-table tbody tr td.text-left { text-align:left; }
        .trial-balance-table tbody tr td:nth-child(2) { white-space: normal; word-wrap: break-word; } /* ACCOUNT TITLE - allow wrapping */
        .trial-balance-table tbody tr td:nth-child(6) { white-space: normal; word-wrap: break-word; } /* SUB SUB HEAD - allow wrapping */
        .trial-balance-total-row { background:#FEF3C7 !important; font-weight:800 }
        
        /* Parent column borders - thicker lines between main sections */
        .trial-balance-table th.parent-border-right,
        .trial-balance-table td.parent-border-right {
          border-right: 2px solid #EAEAEA !important;
        }
        
        /* Column width distribution - optimized for balanced spacing */
        .trial-balance-table th:nth-child(1), .trial-balance-table td:nth-child(1) { width: 6%; } /* CODE */
        .trial-balance-table th:nth-child(2), .trial-balance-table td:nth-child(2) { width: 18%; } /* ACCOUNT TITLE */
        .trial-balance-table th:nth-child(3), .trial-balance-table td:nth-child(3) { width: 6%; } /* TYPE */
        .trial-balance-table th:nth-child(4), .trial-balance-table td:nth-child(4) { width: 8%; } /* HEAD */
        /* COLUMN WIDTH FIXES */
        .trial-balance-table th:nth-child(5), 
        .trial-balance-table td:nth-child(5) {
          width: 12% !important;   /* SUB HEAD → wider */
        }
        .trial-balance-table th:nth-child(6), 
        .trial-balance-table td:nth-child(6) {
          width: 7% !important;    /* SUB SUB HEAD → narrower */
          white-space: normal;
          word-wrap: break-word;
        }
        .trial-balance-table th:nth-child(7), .trial-balance-table td:nth-child(7) { width: 5%; } /* OPENING DR - increased from 4% */
        .trial-balance-table th:nth-child(8), .trial-balance-table td:nth-child(8) { width: 5%; } /* OPENING CR - increased from 4% */
        .trial-balance-table th:nth-child(9), .trial-balance-table td:nth-child(9) { width: 5%; } /* OPENING BALANCE - increased from 4% */
        .trial-balance-table th:nth-child(10), .trial-balance-table td:nth-child(10) { width: 5%; } /* DEBIT DR - increased from 4% */
        .trial-balance-table th:nth-child(11), .trial-balance-table td:nth-child(11) { width: 5%; } /* CREDIT CR - increased from 4% */
        .trial-balance-table th:nth-child(12), .trial-balance-table td:nth-child(12) { width: 5%; } /* TRANSACTION BALANCE - increased from 4% */
        .trial-balance-table th:nth-child(13), .trial-balance-table td:nth-child(13) { width: 5%; } /* TOTAL DEBIT DR - increased from 4% */
        .trial-balance-table th:nth-child(14), .trial-balance-table td:nth-child(14) { width: 5%; } /* TOTAL CREDIT CR - increased from 4% */
        .trial-balance-table th:nth-child(15), .trial-balance-table td:nth-child(15) { width: 5%; } /* TOTAL - increased from 4% */

        /* View mode - make it compact like print */
        @media screen {
          .trial-balance-wrapper { max-width: 100%; padding: 12px }
          .top-brand { margin-bottom: 6px }
          .company-title { font-size: 16px }
          .trial-balance-main-title { font-size: 18px; margin-top: 6px }
          .divider-strong { margin: 8px 0 }
          .trial-balance-table { font-size: 9px; margin-top: 6px }
          .trial-balance-table th, .trial-balance-table td { padding: 4px }
          .trial-balance-table thead th { font-size: 9px }
          .trial-balance-table tbody tr td { font-size: 9px }
        }

        /* Arabic Right-to-left fixes */
        .rtl { direction: rtl; unicode-bidi: embed }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/reports/trial-balance')} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> Print Report
        </Button>
      </div>

      <div className="trial-balance-wrapper">
        {/* Header */}
        <div className="top-brand">
          <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
            <div><strong>{branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>Address:</strong> {buildAddress(branch) || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Email:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>Phone:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>CR No:</strong> {branch?.crNo || '-'}
            </div>
          </div>

          <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <img src={getLogoPath()} alt="Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
          </div>

          <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px] rtl">
            <div><strong>{branch?.nameAr || branch?.nameEn || '-'}</strong></div>
            <div style={{ marginTop: 4 }}>
              <strong>العنوان:</strong> {branch?.addressAr || buildAddress(branch) || '--'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>البريد الإلكتروني:</strong> {branch?.email || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>هاتف:</strong> {branch?.phone || '-'}
            </div>
            <div style={{ marginTop: 2 }}>
              <strong>رقم تسجيل الشركة:</strong> {branch?.crNo || '-'}
            </div>
          </div>
        </div>

        <div className="divider-strong print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

        {/* Report Title */}
        <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
          <div className="trial-balance-main-title print:text-base" style={{ fontSize: 18, fontWeight: 700 }}>Trail Balance</div>
        </div>

        {/* Trial Balance Table */}
        <table className="trial-balance-table">
          <thead>
            {/* Main Header Row */}
            <tr>
              <th colSpan={6} className="parent-border-right" style={{ textAlign: 'center' }}>
                Account
              </th>
              <th colSpan={3} className="parent-border-right" style={{ textAlign: 'center' }}>
                Opening Balance
              </th>
              <th colSpan={3} className="parent-border-right" style={{ textAlign: 'center' }}>
                Transactions
              </th>
              <th colSpan={3} style={{ textAlign: 'center' }}>
                Balance
              </th>
            </tr>
            {/* Sub-header Row */}
            <tr>
              <th style={{ textAlign: 'left' }}>CODE</th>
              <th style={{ textAlign: 'left' }}>ACCOUNT TITLE</th>
              <th style={{ textAlign: 'left' }}>TYPE</th>
              <th style={{ textAlign: 'left' }}>HEAD</th>
              <th style={{ textAlign: 'left' }}>SUB HEAD</th>
              <th className="parent-border-right" style={{ textAlign: 'left' }}>SUB SUB HEAD</th>
              
              <th style={{ textAlign: 'right' }}>OPENING DR.</th>
              <th style={{ textAlign: 'right' }}>OPENING CR.</th>
              <th className="parent-border-right" style={{ textAlign: 'right' }}>BALANCE</th>
              
              <th style={{ textAlign: 'right' }}>DEBIT DR.</th>
              <th style={{ textAlign: 'right' }}>CREDIT CR.</th>
              <th className="parent-border-right" style={{ textAlign: 'right' }}>BALANCE</th>
              
              <th style={{ textAlign: 'right' }}>TOTAL DEBIT DR.</th>
              <th style={{ textAlign: 'right' }}>TOTAL CREDIT CR.</th>
              <th style={{ textAlign: 'right' }}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {trialBalanceData.length === 0 ? (
              <tr>
                <td colSpan={15} style={{ textAlign: 'center', padding: 16 }}>
                  No trial balance data available
                </td>
              </tr>
            ) : (
              trialBalanceData.map((entry, idx) => (
                <tr key={idx}>
                  <td className="text-left">{entry.code || '-'}</td>
                  <td className="text-left">{entry.account || '-'}</td>
                  <td className="text-left">{entry.type || '-'}</td>
                  <td className="text-left">{entry.head || '-'}</td>
                  <td className="text-left">{entry.subHead || '-'}</td>
                  <td className="text-left parent-border-right">{entry.subSubHead || '-'}</td>
                  
                  <td className="text-right">{formatAmount(entry.oDebit)}</td>
                  <td className="text-right">{formatAmount(entry.oCredit)}</td>
                  <td className="text-right parent-border-right">{formatAmount(entry.oTotal)}</td>
                  
                  <td className="text-right">{formatAmount(entry.aDebit)}</td>
                  <td className="text-right">{formatAmount(entry.aCredit)}</td>
                  <td className="text-right parent-border-right">{formatAmount(entry.aTotal)}</td>
                  
                  <td className="text-right">{formatAmount(entry.cDebit)}</td>
                  <td className="text-right">{formatAmount(entry.cCredit)}</td>
                  <td className="text-right">{formatAmount(entry.cTotal)}</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            {/* Total Row with Yellow Background */}
            <tr className="trial-balance-total-row">
              <td colSpan={5} className="text-left"></td>
              <td className="text-left parent-border-right" style={{ fontWeight: 700 }}>Total</td>
              <td className="text-right">{formatAmount(ledgerTotals.oDebit)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.oCredit)}</td>
              <td className="text-right parent-border-right">{formatAmount(ledgerTotals.oTotal)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.aDebit)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.aCredit)}</td>
              <td className="text-right parent-border-right">{formatAmount(ledgerTotals.aTotal)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.cDebit)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.cCredit)}</td>
              <td className="text-right">{formatAmount(ledgerTotals.cTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
